import React, { useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { addEvent, removeEvent, fixBackgroundScroll } from '../../utils/dom';
import s from './Overlay.scss';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Overlay = ({ variantClasses = [], children, isShowing, closeOverlayHandler = () => {}, label = 'Dialog' }) => {
	let classes = [s.overlay];
	if (variantClasses) classes = [...classes, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c).map((c) => s[c]) : [s[variantClasses.toString()]])];
	if (isShowing) classes = [...classes, s.overlay__open];

	const panelRef = useRef(null);
	const previousFocus = useRef(null);

	useEffect(() => {
		addEvent(document.getElementById('root'), 'click.closeOverlay', (e) => {
			if (isShowing && !e.target.closest('.js_overlay')) {
				e.preventDefault();
				e.stopPropagation();
				closeOverlayHandler(e);
			}
		});

		if (isShowing) {
			fixBackgroundScroll(true);
			previousFocus.current = document.activeElement;
			// Focus first focusable element inside dialog
			const focusable = panelRef.current ? panelRef.current.querySelectorAll(FOCUSABLE) : [];
			if (focusable.length === 1) focusable[0].focus();
			else if (focusable.length > 1) {
				focusable[1].focus();
			}
		} else {
			fixBackgroundScroll(false);
			// Restore focus on close
			if (previousFocus.current) {
				previousFocus.current.focus();
				previousFocus.current = null;
			}
		}

		return () => {
			removeEvent(document.getElementById('root'), 'click.closeOverlay');
			fixBackgroundScroll(false);
		};
	}, [isShowing, closeOverlayHandler]);

	// Focus trap: keep Tab/Shift+Tab within the dialog
	const handleKeyDown = (e) => {
		if (!isShowing || e.key !== 'Tab') return;
		const focusable = panelRef.current ? [...panelRef.current.querySelectorAll(FOCUSABLE)] : [];
		if (focusable.length === 0) return;
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		if (e.shiftKey) {
			if (document.activeElement === first) { e.preventDefault(); last.focus(); }
		} else if (document.activeElement === last) {
			e.preventDefault(); first.focus();
		}
	};

	return (
		<div className={classes.join(' ')} onKeyDown={handleKeyDown} inert={isShowing ? undefined : ''}>
			<div className={s.overlay_closeBackground} onClick={closeOverlayHandler} role="presentation" />
			<div className={[s.overlay_panel, 'js_overlay'].join(' ')} role="dialog" aria-modal="true" aria-label={label} ref={panelRef}>
				<div className={s.overlay_panel_close}>
					<button type="button" className={s.overlay_panel_close_icon} onClick={closeOverlayHandler} aria-label="Close" />
				</div>
				<div className={s.overlay_panel_inner}>
					{ children }
				</div>
			</div>
		</div>
	);
};

export default hot(module)(Overlay);
